import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { FileText, Upload, Trash2, Eye, Loader2, ShieldCheck } from 'lucide-react';

const KINDS = [
  { kind: 'ifa', label: 'IFA' },
  { kind: 'pra', label: 'PRA' },
];

const fmtDt = (iso) => iso
  ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })
  : '';

export const NdaRefDocsCard = () => {
  const [refs, setRefs] = useState(null);
  const [busy, setBusy] = useState(null);
  const inputs = { ifa: useRef(null), pra: useRef(null) };

  const load = () => api.getNdaRefs().then(r => setRefs(r.data.refs)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (kind, file) => {
    if (!file) return;
    setBusy(kind);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.uploadNdaRef(kind, fd);
      toast.success(`${kind.toUpperCase()} hochgeladen — wird ab jetzt bei jeder NDA-Analyse geprüft`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen'); }
    setBusy(null);
  };

  const remove = async (kind) => {
    setBusy(kind);
    try { await api.deleteNdaRef(kind); toast.success(`${kind.toUpperCase()} entfernt`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    setBusy(null);
  };

  const view = async (kind) => {
    try {
      const r = await api.getNdaRefFile(kind);
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch { toast.error('Dokument konnte nicht geladen werden'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm p-6 space-y-4" data-testid="nda-refs-card">
      <div>
        <h2 className="font-heading text-lg text-foreground flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-violet-400" /> NDA-Referenzdokumente (IFA / PRA)
        </h2>
        <p className="text-xs text-muted-foreground mt-1">
          Broker-NDAs werden per KI gegen diese Verträge geprüft (Broker Deal Inbox → NDA-Analyse).
          Rote Punkte = Änderungsbedarf, gelbe = nur zur Info.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {KINDS.map(({ kind, label }) => {
          const doc = refs?.[kind];
          return (
            <div key={kind} className="border border-border rounded-sm p-3 space-y-2" data-testid={`nda-ref-slot-${kind}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wider text-foreground">{label}</span>
                {doc && <span className="text-[10px] font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5">hinterlegt</span>}
              </div>
              {doc ? (
                <>
                  <p className="text-xs text-foreground/80 flex items-center gap-1.5 truncate">
                    <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> {doc.filename}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{fmtDt(doc.uploaded_at)} · {doc.uploaded_by} · {Math.round((doc.text_chars || 0) / 1000)}k Zeichen</p>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => view(kind)} data-testid={`nda-ref-view-${kind}`}
                      className="rounded-sm text-[11px] h-7">
                      <Eye className="w-3 h-3 mr-1" /> Ansehen
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => inputs[kind].current?.click()} disabled={busy === kind}
                      data-testid={`nda-ref-replace-${kind}`} className="rounded-sm text-[11px] h-7">
                      <Upload className="w-3 h-3 mr-1" /> Ersetzen
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => remove(kind)} disabled={busy === kind}
                      data-testid={`nda-ref-delete-${kind}`}
                      className="rounded-sm text-[11px] h-7 border-red-500/40 text-red-300 hover:bg-red-500/10">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </>
              ) : (
                <Button variant="outline" onClick={() => inputs[kind].current?.click()} disabled={busy === kind}
                  data-testid={`nda-ref-upload-${kind}`} className="rounded-sm text-xs h-8 w-full border-dashed">
                  {busy === kind ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
                  {label} als PDF hochladen
                </Button>
              )}
              <input ref={inputs[kind]} type="file" accept="application/pdf" className="hidden"
                onChange={e => { upload(kind, e.target.files?.[0]); e.target.value = ''; }} />
            </div>
          );
        })}
      </div>
    </div>
  );
};
